import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiTrash2, FiSettings, FiRotateCcw, FiGrid, FiImage, FiSave, FiZap } from 'react-icons/fi';
import './theme.css';
import FlashcardsNav from './FlashcardsNav';
import FlashcardGenerator from './FlashcardGenerator';

const FlashcardCreator = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('manual'); // 'manual' | 'ai'
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [cards, setCards] = useState([
    { id: 1, term: '', explanation: '', transliteration: '', example: '' },
    { id: 2, term: '', explanation: '', transliteration: '', example: '' }
  ]);
  const [notification, setNotification] = useState(null);

  // Show notification and auto-hide after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const addCard = () => {
    const newId = Math.max(...cards.map(c => c.id)) + 1;
    setCards([...cards, { id: newId, term: '', explanation: '', transliteration: '', example: '' }]);
  };

  const removeCard = (id) => {
    if (cards.length > 1) {
      setCards(cards.filter(card => card.id !== id));
    }
  };

  const updateCard = (id, field, value) => {
    setCards(cards.map(card => 
      card.id === id ? { ...card, [field]: value } : card
    ));
  };

  const saveDeck = async () => {
    if (!title.trim()) {
      showNotification('Please enter a title for your deck', 'error');
      return;
    }

    if (cards.some(card => !card.term.trim() || !card.explanation.trim())) {
      showNotification('Please fill in all term and explanation fields', 'error');
      return;
    }

    try {
      // Create deck payload
      const deckPayload = {
        deckId: `${title.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`,
        title: title.trim(),
        subject: 'Islamic Studies',
        languageMode: 'mixed',
        tags: ['Islamic Studies', 'Education'],
        cards: cards.map(card => ({
          term: card.term.trim(),
          explanation: card.explanation.trim(),
          transliteration: card.transliteration.trim() || undefined,
          example: card.example.trim() || undefined
        })).filter(card => card.term && card.explanation) // Only include cards with both term and explanation
      };

      // Save to Firestore
      const { doc, setDoc } = await import('firebase/firestore');
      const { db, auth } = await import('../config/firebase-config');
      
      const deckId = deckPayload.deckId;
      const libraryDocRef = doc(db, 'flashcardLibrary', deckId);
      
      const metadata = {
        deck: JSON.stringify(deckPayload),
        tags: deckPayload.tags,
        ownerID: auth.currentUser ? auth.currentUser.uid : 'anonymous',
        createdAt: new Date().toISOString(),
        lastEdited: new Date().toISOString(),
        public: false
      };

      await setDoc(libraryDocRef, metadata);

      // Save to user's personal collection if logged in
      if (auth.currentUser) {
        const userDeckRef = doc(db, 'users', auth.currentUser.uid, 'flashcards', deckId);
        await setDoc(userDeckRef, {
          deckId: deckId,
          addedAt: new Date().toISOString(),
          isOwner: true
        });
      }

      showNotification('Deck saved successfully!');
      navigate(`/flashcards/${deckId}`);
    } catch (error) {
      console.error('Error saving deck:', error);
      showNotification('Failed to save deck. Please try again.', 'error');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'hsl(var(--background))' }}>
      {/* Notification Component */}
      {notification && (
        <div 
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '12px 20px',
            borderRadius: '8px',
            color: 'white',
            fontWeight: '500',
            zIndex: 1000,
            background: notification.type === 'success' ? '#10b981' : '#ef4444',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            animation: 'slideIn 0.3s ease-out'
          }}
        >
          {notification.message}
        </div>
      )}

      <main className="container" style={{ paddingTop: 24, paddingBottom: 48 }}>
        <FlashcardsNav />

        {/* Header */}
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h1 className="text-foreground" style={{ fontSize: 24, fontWeight: 700 }}>Create a new flashcard set</h1>
            <div style={{ display: 'flex', gap: 8 }}>
              <button 
                className="button primary" 
                onClick={saveDeck}
                disabled={!title.trim() || cards.some(card => !card.term.trim() || !card.explanation.trim())}
              >
                <FiSave /> Save Deck
              </button>
            </div>
          </div>

          {/* Title and Description */}
          <div style={{ marginBottom: 16 }}>
            <input
              type="text"
              placeholder="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="border rounded"
              style={{ width: '100%', padding: '12px', marginBottom: 12, fontSize: 16 }}
            />
            <textarea
              placeholder="Add a description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="border rounded"
              style={{ width: '100%', padding: '12px', minHeight: 80, resize: 'vertical' }}
            />
          </div>

          {/* Import Options */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button className="button outline" onClick={() => setTab('ai')}>
              <FiZap /> Create from notes
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="card" style={{ padding: 12, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className={`button ${tab === 'manual' ? 'primary' : 'outline'}`}
              onClick={() => setTab('manual')}
            >
              Manual Create
            </button>
            <button
              className={`button ${tab === 'ai' ? 'primary' : 'outline'}`}
              onClick={() => setTab('ai')}
            >
              AI Generate
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="card" style={{ padding: 16 }}>
          {tab === 'manual' ? (
            <div>

              {/* Cards */}
              <div style={{ display: 'grid', gap: 16 }}>
                {cards.map((card, index) => (
                  <div key={card.id} className="card" style={{ padding: 16, border: '1px solid hsl(var(--border))' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                      <span className="text-muted-foreground" style={{ fontWeight: 600 }}>{index + 1}</span>
                      <button 
                        className="button outline" 
                        onClick={() => removeCard(card.id)}
                        style={{ padding: '4px 8px', fontSize: 12 }}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 16, alignItems: 'start' }}>
                      {/* Term */}
                      <div>
                        <input
                          type="text"
                          placeholder="Enter term"
                          value={card.term}
                          onChange={(e) => updateCard(card.id, 'term', e.target.value)}
                          className="border rounded"
                          style={{ width: '100%', padding: '12px', marginBottom: 4 }}
                        />
                        <div className="text-muted-foreground" style={{ fontSize: 12 }}>TERM</div>
                      </div>

                      {/* Equals sign */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 12 }}>
                        <span style={{ fontSize: 18, color: 'hsl(var(--muted-foreground))' }}>=</span>
                      </div>

                      {/* Definition */}
                      <div>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="text"
                            placeholder="Enter definition"
                            value={card.explanation}
                            onChange={(e) => updateCard(card.id, 'explanation', e.target.value)}
                            className="border rounded"
                            style={{ width: '100%', padding: '12px', marginBottom: 4 }}
                          />
                        </div>
                        <div className="text-muted-foreground" style={{ fontSize: 12 }}>DEFINITION</div>
                      </div>
                    </div>

                    {/* Additional fields for Islamic content */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 12 }}>
                      <div>
                        <input
                          type="text"
                          placeholder="Transliteration (optional)"
                          value={card.transliteration}
                          onChange={(e) => updateCard(card.id, 'transliteration', e.target.value)}
                          className="border rounded"
                          style={{ width: '100%', padding: '8px', fontSize: 14 }}
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          placeholder="Example (optional)"
                          value={card.example}
                          onChange={(e) => updateCard(card.id, 'example', e.target.value)}
                          className="border rounded"
                          style={{ width: '100%', padding: '8px', fontSize: 14 }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Card Button */}
              <button className="button outline" onClick={addCard} style={{ marginTop: 16 }}>
                <FiPlus /> Add a card
              </button>
            </div>
          ) : (
            <FlashcardGenerator />
          )}
        </div>
      </main>
    </div>
  );
};

export default FlashcardCreator; 